import { useDeferredValue, useEffect, useMemo, useState } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useLiveQuery } from "dexie-react-hooks";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowDownUp,
  ChevronDown,
  Database,
  Grid2X2,
  List,
  Table2,
  Wifi,
  WifiOff,
} from "lucide-react";
import { Button, getButtonClassName } from "../../../components/Button";
import { Chip } from "../../../components/Chip";
import { EmptyState } from "../../../components/EmptyState";
import { PageHeader } from "../../../components/PageHeader";
import { ReadinessDial } from "../../../components/ReadinessDial";
import { Segmented } from "../../../components/Segmented";
import { StatusBadge } from "../../../components/StatusBadge";
import { db } from "../../../lib/db";
import { useAppStore } from "../../../store/appStore";
import type { JobPost } from "../../../types";
import { JobCard } from "../components/JobCard";
import { JobFilters, type JobFilterState } from "../components/JobFilters";
import { JobRow } from "../components/JobRow";
import { MetricTile } from "../components/MetricTile";

const initialFilters: JobFilterState = {
  search: "",
  category: "ALL",
  urgency: "ALL",
  readiness: "ALL",
};

type SortKey = "updatedAt" | "readiness" | "budget" | "urgency";
type ViewMode = "list" | "cards" | "table";

function budgetValue(job: JobPost) {
  return job.budgetMax ?? job.budgetMin ?? 0;
}

function formatBudget(job: JobPost): string {
  if (job.budgetMin !== undefined && job.budgetMax !== undefined) {
    return `$${job.budgetMin} to $${job.budgetMax}`;
  }
  if (job.budgetMin !== undefined) {
    return `From $${job.budgetMin}`;
  }
  if (job.budgetMax !== undefined) {
    return `Up to $${job.budgetMax}`;
  }
  return "Not listed";
}

function sortJobs(jobs: JobPost[], sortKey: SortKey) {
  return [...jobs].sort((a, b) => {
    if (sortKey === "readiness") {
      return b.readiness.score - a.readiness.score;
    }
    if (sortKey === "budget") {
      return budgetValue(b) - budgetValue(a);
    }
    if (sortKey === "urgency") {
      const order = { EMERGENCY: 4, URGENT: 3, SOON: 2, ROUTINE: 1 };
      return order[b.urgency] - order[a.urgency];
    }
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });
}

function getInitialView(): ViewMode {
  const value = localStorage.getItem("clearscope.jobs.view");
  return value === "cards" || value === "table" ? value : "list";
}

export function BrowseJobsPage() {
  const jobs = useLiveQuery(() => db.jobs.orderBy("updatedAt").reverse().toArray(), [], []);
  const offline = useAppStore((state) => state.offline);
  const [filters, setFilters] = useState<JobFilterState>(initialFilters);
  const [sortKey, setSortKey] = useState<SortKey>("updatedAt");
  const [view, setView] = useState<ViewMode>(getInitialView);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const deferredSearch = useDeferredValue(filters.search);

  useEffect(() => {
    localStorage.setItem("clearscope.jobs.view", view);
  }, [view]);

  useEffect(() => {
    const query = window.matchMedia("(max-width: 767px)");
    const syncView = () => {
      if (query.matches && view === "table") {
        setView("cards");
      }
    };

    syncView();
    query.addEventListener("change", syncView);
    return () => query.removeEventListener("change", syncView);
  }, [view]);

  const filteredJobs = useMemo(() => {
    const search = deferredSearch.trim().toLowerCase();
    return jobs.filter((job) => {
      const matchesSearch = search
        ? [job.title, job.summary, job.neighborhood, job.city]
            .join(" ")
            .toLowerCase()
            .includes(search)
        : true;
      const matchesCategory = filters.category === "ALL" || job.category === filters.category;
      const matchesUrgency = filters.urgency === "ALL" || job.urgency === filters.urgency;
      const matchesReadiness =
        filters.readiness === "ALL" || job.readiness.level === filters.readiness;
      return matchesSearch && matchesCategory && matchesUrgency && matchesReadiness;
    });
  }, [deferredSearch, filters.category, filters.readiness, filters.urgency, jobs]);

  const sortedJobs = useMemo(() => sortJobs(filteredJobs, sortKey), [filteredJobs, sortKey]);
  const averageReadiness = sortedJobs.length
    ? Math.round(
        sortedJobs.reduce((total, job) => total + job.readiness.score, 0) / sortedJobs.length,
      )
    : 0;
  const highRiskCount = sortedJobs.filter(
    (job) => job.readiness.level === "RED" || job.riskFlags.length > 0,
  ).length;
  const emergencyCount = sortedJobs.filter((job) => job.urgency === "EMERGENCY").length;
  const sparkline = useMemo(
    () => sortedJobs.slice(0, 12).map((job) => job.readiness.score),
    [sortedJobs],
  );
  const readinessBuckets = useMemo(
    () => ({
      GREEN: sortedJobs.filter((job) => job.readiness.level === "GREEN").length,
      AMBER: sortedJobs.filter((job) => job.readiness.level === "AMBER").length,
      RED: sortedJobs.filter((job) => job.readiness.level === "RED").length,
    }),
    [sortedJobs],
  );

  const appliedFilters = [
    filters.search ? ["Search", filters.search] : null,
    filters.category !== "ALL" ? ["Category", filters.category.replace(/_/g, " ")] : null,
    filters.urgency !== "ALL" ? ["Urgency", filters.urgency] : null,
    filters.readiness !== "ALL" ? ["Detail", filters.readiness] : null,
  ].filter(Boolean) as [string, string][];

  function clearAppliedFilter(label: string) {
    if (label === "Search") {
      setFilters({ ...filters, search: "" });
    }
    if (label === "Category") {
      setFilters({ ...filters, category: "ALL" });
    }
    if (label === "Urgency") {
      setFilters({ ...filters, urgency: "ALL" });
    }
    if (label === "Detail") {
      setFilters({ ...filters, readiness: "ALL" });
    }
  }

  return (
    <div className="stack-section">
      <PageHeader
        action={
          <Chip size="md">
            {sortedJobs.length} jobs - {highRiskCount} need review - {averageReadiness} avg
          </Chip>
        }
        eyebrow="Open jobs"
        title="Find work near you"
      >
        Real jobs from real homeowners. No subscription, no per-lead fees, no bidding wars.
      </PageHeader>

      {saveMessage ? (
        <div
          className="rounded-card border border-success-border bg-success-bg px-4 py-3 text-sm font-semibold text-success shadow-[var(--shadow-card)]"
          role="status"
        >
          {saveMessage}
        </div>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricTile
          icon={<Database aria-hidden="true" className="h-5 w-5" />}
          label="Saved jobs"
          value={jobs.length}
          detail="On this device"
          tone="accent"
          sparkline={jobs.slice(0, 12).map((job) => job.scopeItems.length)}
        />
        <MetricTile
          label="Avg. detail"
          value={averageReadiness}
          suffix="/100"
          detail={`Across ${sortedJobs.length} visible jobs`}
          tone={averageReadiness >= 80 ? "success" : averageReadiness >= 55 ? "warning" : "danger"}
          sparkline={sparkline}
        />
        <MetricTile
          label="Needs review"
          value={highRiskCount}
          detail="May need a licensed pro"
          tone={highRiskCount ? "warning" : "success"}
          sparkline={sortedJobs.slice(0, 12).map((job) => job.riskFlags.length)}
        />
        <MetricTile
          icon={
            offline ? (
              <WifiOff aria-hidden="true" className="h-5 w-5" />
            ) : (
              <Wifi aria-hidden="true" className="h-5 w-5" />
            )
          }
          label="Connection"
          value={offline ? "Offline" : "Online"}
          detail={`${emergencyCount} emergency jobs in view`}
          tone={offline ? "warning" : "success"}
          sparkline={sortedJobs.slice(0, 12).map((job) => (job.urgency === "EMERGENCY" ? 100 : 45))}
        />
      </div>

      <BoardSignalPanel
        buckets={readinessBuckets}
        highRiskCount={highRiskCount}
        job={sortedJobs[0]}
        total={sortedJobs.length}
      />

      <div className="grid gap-6 lg:grid-cols-[18rem_1fr] lg:items-start xl:grid-cols-[19rem_1fr]">
        <JobFilters
          filters={filters}
          onChange={setFilters}
          onSaveView={() => {
            setSaveMessage("Saved locally.");
            window.setTimeout(() => setSaveMessage(null), 1800);
          }}
        />
        <section className="space-y-4">
          <div className="card-hairline grid gap-3 p-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <output className="text-sm font-semibold text-text-muted" aria-live="polite">
                {filteredJobs.length} jobs match - {highRiskCount} need review
              </output>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Job board controls">
                <Segmented
                  ariaLabel="View mode"
                  options={[
                    { value: "list", label: "List", icon: List },
                    { value: "cards", label: "Cards", icon: Grid2X2 },
                    { value: "table", label: "Table", icon: Table2 },
                  ]}
                  value={view}
                  onChange={setView}
                />
                <SortMenu sortKey={sortKey} onSort={setSortKey} />
              </div>
            </div>
            {appliedFilters.length ? (
              <div className="flex flex-wrap gap-2 border-t border-border pt-3">
                {appliedFilters.map(([label, value]) => (
                  <Chip
                    key={`${label}-${value}`}
                    dismissLabel={`Clear ${label} filter`}
                    onDismiss={() => clearAppliedFilter(label)}
                  >
                    {label}: {value}
                  </Chip>
                ))}
                <Button size="sm" variant="ghost" onClick={() => setFilters(initialFilters)}>
                  Clear filters
                </Button>
              </div>
            ) : null}
          </div>

          {sortedJobs.length ? (
            view === "cards" ? (
              <div className="grid gap-5 xl:grid-cols-2">
                {sortedJobs.map((job) => (
                  <JobCard key={job.id} job={job} />
                ))}
              </div>
            ) : view === "table" ? (
              <JobTable jobs={sortedJobs} />
            ) : (
              <div className="grid gap-3">
                {sortedJobs.map((job) => (
                  <JobRow key={job.id} job={job} />
                ))}
              </div>
            )
          ) : (
            <EmptyState
              action={
                <Link className={getButtonClassName("primary")} to="/post">
                  Post a job
                </Link>
              }
              title="No jobs match your filters"
              variant="search"
            >
              Try clearing your filters, or post a new job to get quotes from local pros.
            </EmptyState>
          )}
        </section>
      </div>
    </div>
  );
}

function BoardSignalPanel({
  job,
  buckets,
  total,
  highRiskCount,
}: {
  job?: JobPost;
  buckets: Record<"GREEN" | "AMBER" | "RED", number>;
  total: number;
  highRiskCount: number;
}) {
  if (!job) {
    return null;
  }

  const rows = [
    { label: "Green", value: buckets.GREEN, className: "bg-success" },
    { label: "Amber", value: buckets.AMBER, className: "bg-warning" },
    { label: "Red", value: buckets.RED, className: "bg-danger" },
  ];

  return (
    <section className="command-panel command-panel-muted grid gap-5 p-5 md:p-6 lg:grid-cols-[minmax(0,1fr)_24rem]">
      <div className="grid gap-4 md:grid-cols-[auto_minmax(0,1fr)] md:items-center">
        <ReadinessDial
          caption="Highest signal"
          label="Detail"
          level={job.readiness.level}
          score={job.readiness.score}
          secondaryRing
          size="lg"
        />
        <div className="min-w-0">
          <p className="page-eyebrow">Best match in view</p>
          <p className="mt-2 font-sans text-3xl font-semibold leading-tight text-text-strong">
            {job.title}
          </p>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-text-muted">{job.summary}</p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <StatusBadge
              dotPosition="trailing"
              label={`${job.readiness.level} ${job.readiness.score}`}
              level={job.readiness.level}
            />
            <Chip tone={highRiskCount ? "warning" : "success"}>{highRiskCount} need review</Chip>
            <Chip>{formatBudget(job)}</Chip>
            <Link
              className={getButtonClassName("secondary", "ml-auto", "sm")}
              to={`/jobs/${job.id}`}
            >
              Open job
            </Link>
          </div>
        </div>
      </div>
      <div className="rounded-card border border-border bg-surface-raised p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="page-eyebrow">Detail mix</p>
            <p className="mt-1 text-sm font-semibold text-text-muted">
              {total} visible jobs after filters
            </p>
          </div>
          <span className="cell-label rounded-pill border border-border bg-paper px-3 py-1">
            On device
          </span>
        </div>
        <div className="mt-5 grid gap-3">
          <div className="grid grid-cols-3 gap-2">
            {rows.map((row) => (
              <div key={row.label}>
                <p className="cell-label">{row.label}</p>
                <p className="mt-1 text-sm font-semibold text-text tabular">{row.value}</p>
              </div>
            ))}
          </div>
          <div className="flex h-3 overflow-hidden rounded-pill border border-border bg-surface-sunken">
            {rows.map((row) => {
              const width = total ? Math.round((row.value / total) * 100) : 0;

              return (
                <span
                  key={row.label}
                  aria-hidden="true"
                  className={`${row.className} min-w-px border-r border-paper/70 last:border-r-0`}
                  style={{ width: `${width}%` }}
                />
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function SortMenu({ sortKey, onSort }: { sortKey: SortKey; onSort: (key: SortKey) => void }) {
  const labels: Record<SortKey, string> = {
    updatedAt: "Recent",
    readiness: "Detail",
    budget: "Budget",
    urgency: "Urgency",
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button
          iconLeft={<ArrowDownUp aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} />}
          iconRight={<ChevronDown aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} />}
          size="sm"
          variant="secondary"
        >
          Sort: {labels[sortKey]}
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          className="dropdown-content z-50 min-w-48 rounded-card border border-border bg-surface-elevated p-1.5 shadow-[var(--shadow-key-2)]"
          sideOffset={8}
        >
          {(["updatedAt", "readiness", "budget", "urgency"] as SortKey[]).map((key) => (
            <DropdownMenu.Item
              key={key}
              className="flex min-h-10 cursor-default select-none items-center rounded-sm px-3 py-2 text-sm font-semibold text-text outline-none transition data-[highlighted]:bg-surface-muted data-[state=checked]:text-accent-700"
              onSelect={() => onSort(key)}
            >
              {labels[key]}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function JobTable({ jobs }: { jobs: JobPost[] }) {
  const navigate = useNavigate();
  const stripe: Record<JobPost["readiness"]["level"], string> = {
    GREEN: "bg-success",
    AMBER: "bg-warning",
    RED: "bg-danger",
  };

  return (
    <div className="overflow-hidden rounded-card border border-border bg-paper shadow-[var(--shadow-key-1)]">
      <div className="overflow-x-auto">
        <table className="min-w-[760px] w-full border-collapse text-left text-sm">
          <thead className="sticky top-0 z-10 bg-surface-muted/90 text-xs uppercase tracking-[0.14em] text-text-subtle backdrop-blur">
            <tr>
              <th className="w-1 px-0 py-3 font-semibold" />
              <th className="px-4 py-3 font-semibold">Job</th>
              <th className="px-4 py-3 font-semibold">Detail</th>
              <th className="px-4 py-3 font-semibold">Location</th>
              <th className="px-4 py-3 font-semibold">Urgency</th>
              <th className="px-4 py-3 font-semibold">Budget</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((job, index) => (
              <tr
                key={job.id}
                className={`cursor-pointer border-t border-border transition hover:bg-surface-raised hover:shadow-[inset_0_0_0_1px_var(--color-accent-200)] ${
                  index % 2
                    ? "bg-[color-mix(in_srgb,var(--color-surface-sunken)_30%,var(--color-paper))]"
                    : ""
                }`}
                tabIndex={0}
                onClick={() => navigate(`/jobs/${job.id}`)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    navigate(`/jobs/${job.id}`);
                  }
                }}
              >
                <td className="p-0">
                  <span className={`block h-full min-h-16 w-1 ${stripe[job.readiness.level]}`} />
                </td>
                <td className="px-4 py-4">
                  <Link
                    className="font-semibold text-text hover:text-accent-700"
                    to={`/jobs/${job.id}`}
                    onClick={(event) => event.stopPropagation()}
                  >
                    {job.title}
                  </Link>
                  <p className="mt-1 line-clamp-1 text-xs font-semibold text-text-subtle">
                    {job.category.replace(/_/g, " ")}
                  </p>
                </td>
                <td className="px-4 py-4 font-semibold tabular">{job.readiness.score}/100</td>
                <td className="px-4 py-4 text-text-muted">{job.neighborhood}</td>
                <td className="px-4 py-4 text-text-muted">{job.urgency}</td>
                <td className="px-4 py-4 font-semibold tabular">{formatBudget(job)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
